import { Box, GlobalStyles } from '@mui/material';
import classNames from 'classnames';
import differenceBy from 'lodash/differenceBy';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { Provider } from 'react-redux';
import { ensureListing } from '../../util/data';
import { hasSameSDKBounds, sdkBoundsToFixedCoordinates } from '../../util/maps';
import { isCordova } from '../../util/mobile/deviceDetection';
import { isMobileSize } from '../../util/mobile/mobile';
import { parse } from '../../util/urlHelpers';
import PoiLayerControl from '../Map/PoiLayerControl';
import { groupedByCoordinates, reducedToArray } from './SearchMap.helpers.js';
import SearchMapGroupLabel from './SearchMapGroupLabel/SearchMapGroupLabel';
import SearchMapIndicator from './SearchMapIndicator';
import SearchMapInfoCard from './SearchMapInfoCard/SearchMapInfoCard';
import SearchMapMarkerLabel from './SearchMapMarkerLabel/SearchMapMarkerLabel';
import SearchMapPriceLabel from './SearchMapPriceLabel/SearchMapPriceLabel';
import {
  fitMapToBounds,
  getMapBounds,
  getMapCenter,
  INFO_CARD_HANDLE,
  LABEL_HANDLE,
} from './SearchMapWithMapbox.util';

const BOUNDS_FIXED_PRECISION = 8;

// Stable class names for the imperatively created (detached) marker / info-card
// DOM nodes. These are added via `classList` to nodes built with
// `document.createElement`, so they cannot use the `sx` prop — their styles are
// injected as global rules below via <GlobalStyles>.
const LABEL_CONTAINER_CLASS = 'SearchMapWithMapbox-labelContainer';
const ACTIVE_LABEL_CLASS = 'SearchMapWithMapbox-activeLabel';
const INFO_CARD_CONTAINER_CLASS = 'SearchMapWithMapbox-infoCardContainer';

// Global rules for the detached marker / info-card containers (the imperative
// `classList`-applied classes). Plain object (no theme callback) — prefer static
// rules here for any imperatively-moved node that lives outside the MUI theme tree.
const markerGlobalStyles = {
  [`.${LABEL_CONTAINER_CLASS}:hover`]: { zIndex: 1 },
  [`.${LABEL_CONTAINER_CLASS}:focus`]: { outline: 'none' },
  [`.${ACTIVE_LABEL_CLASS}`]: { zIndex: 1 },
  [`.${INFO_CARD_CONTAINER_CLASS}`]: { zIndex: 1 },
};

// Static sx for elements rendered in this component's own React tree. This
// component is rendered via React.createPortal from MapProvider, inside the
// app's Router/Theme/Intl — but the Mapbox marker containers are DOM nodes
// appended imperatively (outside any React tree), so prefer static `sx` /
// <GlobalStyles> for styles that target those nodes.
const fullAreaSx = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  // Mapbox adds the `.mapboxgl-map` class (position: relative, no height) to this container.
  // The original .fullArea CSS-module class won that cascade and stayed absolute+inset:0; an
  // emotion sx class loses cascade order to mapbox-gl.css and falls back to relative → h0.
  // Explicit 100% sizing fills the parent (#search-map, fixed h:full) either way, so the map shows.
  width: '100%',
  height: '100%',
};

const mapInitFailedSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  padding: '24px',
  textAlign: 'center',
  fontSize: '14px',
};

// Module-scope so their references are stable across renders —
// re-adding the same (type, handler, options) tuple to
// `document.addEventListener` is a no-op, which is what we want for
// the iOS-Safari pinch-guard. With a render-scoped closure, each
// render added fresh listeners that accumulated indefinitely.
const handleMobilePinchZoom = (e) => {
  e.preventDefault();
  // A hack to prevent pinch zoom gesture in mobile Safari —
  // otherwise, pinch zoom would zoom both _map and the document.
  document.body.style.zoom = 0.99;
};

const handleDoubleClickOnInfoCard = (e) => {
  e.stopPropagation();
};

/**
 * Return price labels grouped by listing locations.
 * This is a helper function for SearchMapWithMapbox component.
 */
const priceLabelsInLocations = (
  listings,
  activeListingId,
  infoCardOpen,
  onListingClicked,
  mapComponentRefreshToken
) => {
  const listingArraysInLocations = reducedToArray(groupedByCoordinates(listings));
  const priceLabels = listingArraysInLocations.reverse().map((listingArr) => {
    const isActive = activeListingId
      ? !!listingArr.find((l) => activeListingId.uuid === l.id.uuid)
      : false;

    // If location contains only one listing, print price label
    if (listingArr.length === 1) {
      const listing = listingArr[0];
      const infoCardOpenIds = Array.isArray(infoCardOpen)
        ? infoCardOpen.map((l) => l.id.uuid)
        : infoCardOpen
          ? [infoCardOpen.id.uuid]
          : [];

      // if the listing is open, don't print price label
      if (infoCardOpen != null && infoCardOpenIds.includes(listing.id.uuid)) {
        return null;
      }

      // Explicit type change to object literal for Google OverlayViews (geolocation is SDK type)
      const { geolocation } = listing.attributes;

      const key = listing.id.uuid;
      return {
        markerId: `price_${key}`,
        location: geolocation,
        type: 'price',
        componentProps: {
          key,
          isActive,
          className: LABEL_HANDLE,
          listing,
          onListingClicked,
          mapComponentRefreshToken,
        },
      };
    }

    // Explicit type change to object literal for Google OverlayViews (geolocation is SDK type)
    const firstListing = ensureListing(listingArr[0]);
    const geolocation = firstListing.attributes.geolocation;

    const key = listingArr[0].id.uuid;
    return {
      markerId: `group_${key}`,
      location: geolocation,
      type: 'group',
      componentProps: {
        key,
        isActive,
        className: LABEL_HANDLE,
        listings: listingArr,
        onListingClicked,
        mapComponentRefreshToken,
      },
    };
  });
  return priceLabels;
};

/**
 * Return info card. This is a helper function for SearchMapWithMapbox component.
 */
const infoCardComponent = (props) => {
  const {
    infoCardOpen,
    onListingInfoCardClicked,
    createURLToListing,
    mapComponentRefreshToken,
    annualPassIsValid,
    urlCategory,
  } = props;
  const listingsArray = Array.isArray(infoCardOpen) ? infoCardOpen : [infoCardOpen];

  if (!infoCardOpen) {
    return null;
  }

  const firstListing = ensureListing(listingsArray[0]);
  const key = firstListing.id.uuid;
  const geolocation = firstListing.attributes.geolocation;

  return {
    markerId: `infoCard_${key}`,
    location: geolocation,
    componentProps: {
      key,
      mapComponentRefreshToken,
      className: INFO_CARD_HANDLE,
      listings: listingsArray,
      onListingInfoCardClicked,
      createURLToListing,
      annualPassIsValid,
      urlCategory,
    },
  };
};

/**
 * SearchMap component using Mapbox as map provider
 */
const SearchMapWithMapbox = ({
  annualPassIsValid = false,
  activeListingId = null,
  bounds = null,
  className = '',
  createURLToListing = () => {},
  id = 'map',
  infoCardOpen = null,
  listings = [],
  mapComponentRefreshToken = null,
  onClick = () => {},
  onListingClicked = () => {},
  onListingInfoCardClicked = () => {},
  onMapLoad = () => {},
  onMapMoveEnd = () => {},
  isActive = true,
  activationToken = 0,
  location = null,
  store = null,
  urlCategory = null,
  tab = 'map',
  searchInProgress = false,
  showLoadingIndicator = false,
}) => {
  const intl = useIntl();

  const _map = useRef(null);
  const mapContainer = useRef(null);
  const [isMapReady, setIsMapReady] = useState(false);

  const currentMarkers = useRef([]);
  const currentInfoCard = useRef(null);
  const viewportBounds = useRef(null);
  // Bumped by the marker-sync effect below so the portals-reading render
  // sees the fresh `currentMarkers.current` / `currentInfoCard.current`.
  // Kept as state (rather than `forceUpdate`-style) so React schedules
  // the render normally instead of eagerly re-running the effect.
  const [markerTick, setMarkerTick] = useState(0);

  const onMoveend = useCallback(() => {
    if (_map.current) {
      if (isActive) {
        const viewportMapBounds = getMapBounds(_map.current);
        const viewportMapCenter = getMapCenter(_map.current);
        const newViewportBounds = sdkBoundsToFixedCoordinates(
          viewportMapBounds,
          BOUNDS_FIXED_PRECISION
        );

        // ViewportBounds from (previous) rendering differ from viewportBounds currently set to _map
        // I.e. user has changed the _map somehow: moved, panned, zoomed, resized
        const viewportBoundsChanged =
          newViewportBounds && !hasSameSDKBounds(viewportBounds.current, newViewportBounds);
        onMapMoveEnd(viewportBoundsChanged, {
          viewportBounds: newViewportBounds,
          viewportMapCenter,
        });
        viewportBounds.current = newViewportBounds;
      }
    }
  }, [onMapMoveEnd, isActive]);

  const [mapInitFailed, setMapInitFailed] = useState(false);

  const initializeMap = useCallback(() => {
    const { offsetHeight, offsetWidth } = mapContainer.current;
    const hasDimensions = offsetHeight > 0 && offsetWidth > 0;
    if (hasDimensions && !isMapReady) {
      try {
        _map.current = new window.mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/vansiteeu/clgm0cbjg00bs01pkd51idzv9',
          scrollZoom: false,
          touchPitch: false,
          pitchWithRotate: false,
          dragRotate: false,
        });
      } catch (e) {
        console.error('Mapbox initialization failed:', e);
        setMapInitFailed(true);
        return;
      }

      _map.current.touchZoomRotate.disableRotation();

      // this._map.on('load', this.handleOnLoad);

      _map.current.on('style.load', () => {
        setMapLanguage(intl.locale);
      });

      const nav = new window.mapboxgl.NavigationControl({ showCompass: false });
      const location = new window.mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        fitBoundsOptions: {
          maxZoom: 10,
        },
        trackUserLocation: true,
      });
      if (isCordova() || isMobileSize()) {
        _map.current.addControl(location, 'top-left');
      }
      _map.current.addControl(nav, 'top-left');
      // Native POI category control, stacked under the zoom control (top-left).
      _map.current.addControl(new PoiLayerControl({ store, intl }), 'top-left');
      _map.current.on('moveend', (e) => {
        // A window 'resize' makes Mapbox auto-resize and fire a moveend that
        // carries the resize event as `originalEvent`. On Android the soft
        // keyboard shrinks the WebView viewport, so tapping the LandingPage
        // search field resized the (persisted) map and hijacked it into a
        // `mapSearch` navigation back to the previous search. A resize is not a
        // user "search this area" gesture — only react to real pointer/wheel moves.
        if (e.originalEvent && e.originalEvent.type !== 'resize') {
          onMoveend();
        }
      });
      // Introduce rerendering after _map is ready (to include labels),
      // but keep the _map out of state life cycle.
      setIsMapReady(true);
    }
  }, [intl.locale, isMapReady, onMoveend]);

  const prevMapComponentRefreshToken = useRef(mapComponentRefreshToken);
  useEffect(() => {
    if (!_map.current && mapContainer.current && tab === 'map') {
      initializeMap();

      // On mobile the map mounts inside ModalInMobile, which renders its
      // children one commit *before* it transitions to the open (visible)
      // state — so the container is still display:none (0×0) when this
      // effect first runs and initializeMap() bails on its hasDimensions
      // check. This effect's deps don't change when the modal later opens,
      // so without a retry the map would never initialize on first mobile
      // load (it only recovered after a list↔map toggle flipped `tab`).
      // Watch the container and initialize as soon as it has dimensions.
      if (!_map.current && typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => {
          if (_map.current || !mapContainer.current) {
            ro.disconnect();
            return;
          }
          const { offsetWidth, offsetHeight } = mapContainer.current;
          if (offsetWidth > 0 && offsetHeight > 0) {
            initializeMap();
            onMapLoad(_map.current);
            ro.disconnect();
          }
        });
        ro.observe(mapContainer.current);
        return () => ro.disconnect();
      }

      /* Notify parent component that Mapbox _map is loaded */
      onMapLoad(_map.current);
    } else if (prevMapComponentRefreshToken !== mapComponentRefreshToken) {
      onMapLoad(_map.current);
    }
  }, [initializeMap, mapComponentRefreshToken, onMapLoad, tab]);

  // Mapbox sizes its canvas to the container at init and only auto-resizes on window
  // 'resize' events — not when the container itself changes size. The flex map column
  // settles to its final width a commit or two after the map initializes (and the
  // reusable node is moved between layouts), leaving the canvas narrower than its
  // container. Observe the container for the map's lifetime and resize to match.
  useEffect(() => {
    if (!isMapReady || !mapContainer.current || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const ro = new ResizeObserver(() => {
      _map.current?.resize();
    });
    ro.observe(mapContainer.current);
    return () => ro.disconnect();
  }, [isMapReady]);

  // A re-claim (provider moved the node into a new slot) bumps activationToken.
  // Resize the map to the new slot; do NOT auto-fit — the persisted node keeps its view.
  useEffect(() => {
    if (isMapReady) {
      _map.current?.resize();
    }
  }, [activationToken, isMapReady]);

  useEffect(() => {
    const { mapSearch } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    if (!mapSearch) {
      viewportBounds.current = null;
    }
    if (_map.current) {
      const currentBounds = getMapBounds(_map.current);

      // Do not call fitMapToBounds if bounds are the same.
      // Our bounds are viewport bounds, and fitBounds will try to add margins around those bounds
      // that would result to zoom-loop (bound change -> fitmap -> bounds change -> ...)
      if (!isEqual(bounds, currentBounds) && !viewportBounds.current) {
        fitMapToBounds(_map.current, bounds, { padding: 0, isAutocompleteSearch: true });
      }
    }
  }, [bounds]);

  useEffect(() => {
    setMapLanguage(intl.locale);
  }, [intl.locale]);

  // Attach the iOS-Safari pinch-guard listeners ONCE per mount. The
  // previous version attached them from an inline ref-callback
  // (`ref={(node) => onMount(node)}`), so they were re-added on every
  // render — with a fresh closure of `handleMobilePinchZoom` each
  // time, because addEventListener only dedupes when the handler
  // reference is identical. Several hundred panning-induced renders
  // later, `document` had hundreds of live gesture listeners, each
  // firing on every touch event — that's the "gets worse and worse"
  // the user was seeing. The handler is now a module-level function
  // (stable reference) and the attach/detach runs in a mount-only
  // useEffect, with symmetric cleanup on unmount.
  useEffect(() => {
    document.addEventListener('gesturestart', handleMobilePinchZoom, false);
    document.addEventListener('gesturechange', handleMobilePinchZoom, false);
    document.addEventListener('gestureend', handleMobilePinchZoom, false);
    return () => {
      currentInfoCard?.current?.markerContainer?.removeEventListener(
        'dblclick',
        handleDoubleClickOnInfoCard
      );
      document.removeEventListener('gesturestart', handleMobilePinchZoom, false);
      document.removeEventListener('gesturechange', handleMobilePinchZoom, false);
      document.removeEventListener('gestureend', handleMobilePinchZoom, false);
    };
  }, []);

  // Stable ref callback — attaching listeners from here was the leak,
  // so now this only stores the node reference.
  const onMount = useCallback((node) => {
    mapContainer.current = node;
  }, []);

  const setMapLanguage = (language) => {
    try {
      const layers = _map.current.getStyle().layers;

      for (const layer of layers) {
        if (layer.id.includes('label') && layer.type === 'symbol' && layer.layout['text-field']) {
          const textField = layer.layout['text-field'];
          const updateText = (text) => {
            if (text.includes('name_')) {
              return text.replace(/_.*/g, `_${language}`);
            }
            return text;
          };
          const updatedTextField = textField.map((field) => {
            if (Array.isArray(field)) {
              return field.map(updateText);
            }
            return updateText(field);
          });
          _map.current.setLayoutProperty(layer.id, 'text-field', updatedTextField);
        }
      }
    } catch (e) {
      // layers not loaded yet
    }
  };

  // (`handleMobilePinchZoom` and `handleDoubleClickOnInfoCard` moved
  // to module scope below so their references are stable — see the
  // mount-only `useEffect` above for the gesture leak context.)

  const handleOnLoad = () => {
    // const currentZoom = this._map.getZoom();
    // this.setState({ currentZoomLevel: currentZoom });
    //
    // if (this.props.location.pathname.includes('bookmarks')) return;
    // if (!this.props.annualPassIsValid) return;
    // fetch(geolocationJsonUrl)
    //   .then(response => response.arrayBuffer())
    //   .then(buffer => pako.ungzip(buffer, { to: 'string' }))
    //   .then(stringJSON => {
    //     this._map.addSource('listings', {
    //       type: 'geojson',
    //       data: JSON.parse(stringJSON),
    //       cluster: true,
    //       clusterMaxZoom: 9,
    //       clusterRadius: 100,
    //     })
    //
    //     this._map.addLayer({
    //       id: 'clusters', type: 'circle', source: 'listings', filter: ['has', 'point_count'], paint: {
    //         'circle-color': '#0e3b43',
    //         'circle-radius': ['step', ['get', 'point_count'], 20, 25, 25, 50, 30, 100, 35, 200, 35, 500, 40,]
    //       }
    //     });
    //
    //     this._map.addLayer({
    //       id: 'cluster-count', type: 'symbol', source: 'listings', filter: ['has', 'point_count'], layout: {
    //         'text-field': ['get', 'point_count_abbreviated'],
    //         'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    //         'text-size': 16,
    //       }, paint: {
    //         'text-color': '#ffffff'
    //       }
    //     });
    //
    //     this._map.on('click', 'clusters', (e) => {
    //       const features = this._map.queryRenderedFeatures(e.point, {
    //         layers: ['clusters']
    //       });
    //       const clusterId = features[0].properties.cluster_id;
    //       this._map.getSource('listings').getClusterExpansionZoom(clusterId, (err, zoom) => {
    //         if (err) return;
    //         this._map.easeTo({
    //           center: features[0].geometry.coordinates, zoom
    //         });
    //       });
    //     });
    //
    //     this._map.on('mouseenter', 'clusters', () => {
    //       this._map.getCanvas().style.cursor = 'pointer';
    //     });
    //     this._map.on('mouseleave', 'clusters', () => {
    //       this._map.getCanvas().style.cursor = '';
    //     });
    //   })
  };

  // Stable keys derived from volatile inputs. The effect below uses
  // these so it only fires when the actual set of markers / the
  // selected listing / the info-card listing changes — NOT on every
  // parent re-render that hands us a fresh `listings` array reference
  // (common: Redux `marketplaceData.entities` churns on unrelated
  // fetches) or a fresh callback closure.
  const listingIdsKey = useMemo(
    () =>
      (listings || [])
        .map((l) => l?.id?.uuid)
        .filter(Boolean)
        .sort()
        .join('|'),
    [listings]
  );
  const activeListingKey = activeListingId?.uuid || '';
  const infoCardKey = useMemo(() => {
    if (!infoCardOpen) return '';
    const arr = Array.isArray(infoCardOpen) ? infoCardOpen : [infoCardOpen];
    return arr
      .map((l) => l?.id?.uuid)
      .filter(Boolean)
      .sort()
      .join('|');
  }, [infoCardOpen]);

  // Latest-props mirror so the marker-sync effect can read the current
  // values without subscribing to them as deps. Callbacks and the
  // listings array arrive un-memoized from SearchPage today; tying the
  // effect to their identity would reintroduce the per-render churn.
  const syncRefs = useRef({});
  syncRefs.current = {
    listings,
    activeListingId,
    infoCardOpen,
    onListingClicked,
    onListingInfoCardClicked,
    createURLToListing,
    annualPassIsValid,
    urlCategory,
  };

  // Single effect that replaces the old render-body imperative block.
  // Runs only when a derived key actually changes. Bumps `markerTick`
  // at the end so the portals-reading render below sees the updated
  // `currentMarkers.current` / `currentInfoCard.current`.
  useEffect(() => {
    if (!_map.current || !isMapReady) return;
    const r = syncRefs.current;

    const labels = priceLabelsInLocations(
      r.listings,
      r.activeListingId,
      r.infoCardOpen,
      r.onListingClicked,
      mapComponentRefreshToken
    );

    const removableMarkers = differenceBy(currentMarkers.current, labels, 'markerId');
    removableMarkers.forEach((rm) => rm.marker?.remove());

    const prevById = new Map(currentMarkers.current.map((m) => [m.markerId, m]));
    currentMarkers.current = labels
      .filter((v) => v != null)
      .map((m) => {
        const existing = prevById.get(m.markerId);
        if (existing && existing.marker) {
          const { marker, markerContainer } = existing;
          return { ...m, markerContainer, marker };
        }
        const markerContainer = document.createElement('div');
        markerContainer.setAttribute('id', m.markerId);
        markerContainer.classList.add(LABEL_CONTAINER_CLASS);
        // `contain: layout style` tells the browser to scope style /
        // layout invalidation to this subtree. Mapbox rewrites
        // `transform` on every marker container on every frame during
        // pan, which would otherwise force the style engine to re-match
        // all 100+ marker subtrees against every selector in the page
        // (the trace showed 200-element, 70–300 ms recalc batches).
        // Not `paint` — the caret below the pill extends outside the
        // container's box and must not be clipped.
        markerContainer.style.contain = 'layout style';
        const marker = new window.mapboxgl.Marker(markerContainer, { anchor: 'bottom' })
          .setLngLat([m.location.lng, m.location.lat])
          .addTo(_map.current);
        return { ...m, markerContainer, marker };
      });

    if (r.infoCardOpen) {
      const infoCard = infoCardComponent({
        infoCardOpen: r.infoCardOpen,
        onListingInfoCardClicked: r.onListingInfoCardClicked,
        createURLToListing: r.createURLToListing,
        mapComponentRefreshToken,
        annualPassIsValid: r.annualPassIsValid,
        urlCategory: r.urlCategory,
      });

      // Reuse the existing popup when the selected listing didn't
      // change — just refresh `componentProps` (picked up by the
      // portal) and skip the teardown/recreate. Previously every
      // render tore down the popup, which on top of the Mapbox
      // DOM churn also reset the dynamic anchor computation.
      if (infoCard && currentInfoCard.current?.markerId === infoCard.markerId) {
        currentInfoCard.current.componentProps = infoCard.componentProps;
      } else {
        if (currentInfoCard.current?.marker) {
          currentInfoCard.current.markerContainer?.removeEventListener(
            'dblclick',
            handleDoubleClickOnInfoCard
          );
          currentInfoCard.current.marker.remove();
        }
        const infoCardContainer = document.createElement('div');
        infoCardContainer.setAttribute('id', infoCard.markerId);
        infoCardContainer.classList.add(INFO_CARD_CONTAINER_CLASS);
        infoCardContainer.addEventListener('dblclick', handleDoubleClickOnInfoCard, false);
        const popup = new window.mapboxgl.Popup({
          className: 'search-map-info-card-popup',
          closeButton: false,
          closeOnClick: false,
          closeOnMove: false,
          maxWidth: 'none',
          offset: 16,
        })
          .setLngLat([infoCard.location.lng, infoCard.location.lat])
          .setDOMContent(infoCardContainer)
          .addTo(_map.current);
        currentInfoCard.current = {
          ...infoCard,
          markerContainer: infoCardContainer,
          marker: popup,
        };
      }
    } else if (currentInfoCard.current) {
      currentInfoCard.current.markerContainer?.removeEventListener(
        'dblclick',
        handleDoubleClickOnInfoCard
      );
      currentInfoCard.current.marker?.remove();
      currentInfoCard.current = null;
    }

    setMarkerTick((n) => n + 1);
     
  }, [listingIdsKey, activeListingKey, infoCardKey, mapComponentRefreshToken, isMapReady]);

  // `markerTick` is read only implicitly — it exists to trigger the
  // portal-rendering re-render after the effect mutates our refs.
  void markerTick;

  const renderMarkerComponent = (type, componentProps) => {
    const { key, ...rest } = componentProps;
    switch (type) {
      case 'price':
        return annualPassIsValid ? (
          <SearchMapMarkerLabel key={key} {...rest} />
        ) : (
          <SearchMapPriceLabel key={key} {...rest} />
        );
      case 'group':
        return <SearchMapGroupLabel key={key} {...rest} />;
      default:
        return null;
    }
  };

  // Batch activeLabel class updates outside of render to avoid layout thrashing
  useEffect(() => {
    currentMarkers.current.forEach((m) => {
      const isActive = activeListingId && activeListingId.uuid === m.componentProps.key;
      if (isActive) {
        m.markerContainer.classList.add(ACTIVE_LABEL_CLASS);
      } else {
        m.markerContainer.classList.remove(ACTIVE_LABEL_CLASS);
      }
    });
  }, [activeListingId]);

  if (mapInitFailed) {
    return (
      <Box className={classNames(className)} component="div" id={id} sx={fullAreaSx}>
        <Box component="div" sx={mapInitFailedSx}>
          {intl.formatMessage({ id: 'SearchMapWithMapbox.mapNotSupported' })}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className={classNames(className)}
      component="div"
      id={id}
      onClick={onClick}
      ref={(node) => onMount(node)}
      sx={fullAreaSx}
    >
      <GlobalStyles styles={markerGlobalStyles} />
      {searchInProgress && showLoadingIndicator ? <SearchMapIndicator /> : null}
      {currentMarkers.current.map((m) => {
        const isMapReadyForMarkers = _map && m.markerContainer;
        // DOM node that should be used as portal's root
        const portalDOMContainer = isMapReadyForMarkers
          ? document.getElementById(m.markerContainer.id)
          : null;

        if (isMapReadyForMarkers) {
          return ReactDOM.createPortal(
            <Provider store={store}>{renderMarkerComponent(m.type, m.componentProps)}</Provider>,
            portalDOMContainer
          );
        }
        return null;
      })}
      {mapContainer.current && currentInfoCard.current
        ? ReactDOM.createPortal(
            <Provider store={store}>
              {/* `anchor: "none"` hides the card's built-in caret so the
                  mapbox-gl Popup wrapping the portal container draws the only arrow. */}
              <SearchMapInfoCard anchor="none" {...currentInfoCard.current.componentProps} />
            </Provider>,
            currentInfoCard.current.markerContainer
          )
        : null}
    </Box>
  );
};

export default SearchMapWithMapbox;
